import { useQuery } from '@apollo/client';
import { IColumn, IconButton, Stack, Text, TooltipHost } from '@fluentui/react';
import { GenericAttachForm } from 'common/components/ActionMenu/Attach/AttachFormModal';
import { AmountTextView } from 'common/components/AmountView/AmountTextView';
import { DocumentDataCallout } from 'common/components/AttachDocumentModal/DocumentDataCallout';
import { DocumentViewModalState } from 'common/components/DocumentList';
import { AppliableInvoiceSigningDocuments_appliableInvoiceSigningDocuments_nodes } from 'common/components/Modules/TransactionEdit/FormView/ActionMenu/AttachForm/__generated__/AppliableInvoiceSigningDocuments';
import { TABLE_ROWS } from 'common/constants';
import { getGlobalDateFormat } from 'common/utils/dateFormats';
import React, { useCallback, useMemo } from 'react';
import {
  ApprovalExtendedDocumentTypes,
  ApprovalExtendedDocumentTypesVariables,
} from '../UploadForm/__generated__/ApprovalExtendedDocumentTypes';
import { AttachableApprovalExtendedDocuments } from './__generated__/AttachableApprovalExtendedDocuments';
import { AttachApprovalExtendedDocumentVariables } from './__generated__/AttachApprovalExtendedDocument';
import { columns } from './column.data';
import { useStyles } from './index.styles';
import { toOrderByVariable } from './utils';

import APPLIABLE_APPROVAL_DOCUMENTS from './AttachableApprovalExtendedDocuments.graphql';
import APPLY_APPROVAL_DOCUMENTS from './AttachApprovalExtendedDocument.graphql';
import AVAILABLE_DOCUMENT_TYPES from '../UploadForm/ApprovalExtendedDocumentTypes.graphql';

type AttachableDataType =
  AppliableInvoiceSigningDocuments_appliableInvoiceSigningDocuments_nodes;

interface AttachFormProps {
  entityApprovalId: string;
  onRefetch: () => void;
}

export const AttachForm: React.FC<AttachFormProps> = ({
  entityApprovalId,
  onRefetch,
}) => {
  const styles = useStyles();

  const { data: AvailableDocumentTypes } = useQuery<
    ApprovalExtendedDocumentTypes,
    ApprovalExtendedDocumentTypesVariables
  >(AVAILABLE_DOCUMENT_TYPES, {
    variables: {
      entityApprovalId: entityApprovalId,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const getDocumentTypeOptions = useCallback(
    (data: ApprovalExtendedDocumentTypes | undefined) => {
      return (
        data?.approvalExtendedDocumentTypes?.nodes.map((doctype) => ({
          key: doctype.id,
          text: doctype.documentType || '',
          title: doctype.documentType || undefined,
          disabled: undefined,
        })) || []
      );
    },
    []
  );

  const canSelectItem = useCallback(() => true, []);

  const transformData = useMemo(
    () => (data: AttachableApprovalExtendedDocuments | undefined) =>
      data?.attachableApprovalExtendedDocuments?.nodes.map(
        (documents) =>
          ({
            ...documents,
          } as AttachableDataType)
      ) || [],
    []
  );

  const getMutationInput = useCallback(
    (selectedItems: AttachableDataType[], entityId: string) => {
      const entityDocumentIds = selectedItems.map((item) => item.id);
      return {
        input: {
          entityApprovalId: entityId,
          entityDocumentId: entityDocumentIds,
        },
      } as AttachApprovalExtendedDocumentVariables;
    },
    []
  );

  const onRenderItemColumn = useCallback(
    (
      item?: AttachableDataType,
      _index?: number,
      column?: IColumn,
      openDocumentViewModal?: (state: DocumentViewModalState) => void
    ) => {
      if (!item || !column) return undefined;
      const fieldContent = item[
        column.fieldName as keyof AttachableDataType
      ] as string | null;

      switch (column.key) {
        case 'indexAmount':
        case 'available':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <AmountTextView
                className={styles.contentColumnAlignRight}
                value={fieldContent}
              />
            </Stack>
          );
        case 'used':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <AmountTextView
                className={styles.contentColumnAlignRight}
                value={item.documentAppliedAmounts?.usedTotal!}
              />
            </Stack>
          );
        case 'view':
          const viewDocumentVisible =
            item._isProtected! || item._fileViewer !== 'browser';
          return (
            <Stack
              className={styles.columnHeight}
              tokens={{ childrenGap: 10 }}
              horizontal
              verticalAlign="center"
            >
              <TooltipHost content="View" id="tooltipId">
                <IconButton
                  disabled={viewDocumentVisible}
                  iconProps={{ iconName: 'View' }}
                  onClick={() =>
                    openDocumentViewModal?.({
                      isOpen: true,
                      title: item.fileReference,
                      entityDocumentId: item.id,
                      _fileType: item._fileType!,
                    })
                  }
                />
              </TooltipHost>
            </Stack>
          );
        case 'fileReference':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <DocumentDataCallout item={item} />
            </Stack>
          );
        case '_uploadDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text>
                {item._uploadDate ? getGlobalDateFormat(item._uploadDate) : ''}
              </Text>
            </Stack>
          );
        default:
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text>{fieldContent}</Text>
            </Stack>
          );
      }
    },
    [styles]
  );

  return (
    <GenericAttachForm
      entity={{
        id: entityApprovalId,
        onRefetch,
      }}
      availableDocumentTypes={AvailableDocumentTypes}
      attachableDocumentsQuery={APPLIABLE_APPROVAL_DOCUMENTS}
      attachDocumentsMutation={APPLY_APPROVAL_DOCUMENTS}
      detailsQuery={APPLIABLE_APPROVAL_DOCUMENTS}
      columns={columns}
      modalWidth={1450}
      getDocumentTypeOptions={getDocumentTypeOptions}
      transformData={transformData}
      canSelectItem={canSelectItem}
      getMutationInput={getMutationInput}
      onRenderItemColumn={onRenderItemColumn}
      initialQueryVariables={{
        first: TABLE_ROWS,
        entityApprovalId: entityApprovalId,
        orderBy: ['_UPLOAD_DATE_DESC', 'PRIMARY_KEY_DESC'],
        after: null, // Ensure fresh data on refetch
      }}
      toOrderByVariable={toOrderByVariable}
    />
  );
};
